import { useEffect, useRef, useState } from "react";
import { UploadSimple, CheckCircle, ArrowsLeftRight, Clock, Sparkle, Coins } from "@phosphor-icons/react";

// Curated activity templates that "pulse" on the dashboard. Uses a small random walk
// to produce fresh events every 4-9 seconds — enough motion to feel alive without noise.
const ENTITIES = [
    "Mobility Distribution — SA #02", "Mobility Distribution — AE #01", "Consumer Finance — EG #03", "Solar Assets — SA #01", "Wind Assets — UK #02",
    "Commercial RE — SA #01", "Hospitals — SA #02", "Electronics — AE #01", "Mobility Distribution — TR #01", "Wind Assets — JP #01",
    "Premium Auto Retail — SA #02", "Premium Auto Retail — AE #01", "Insurance — SA #03", "Solar Assets — ES #01",
];
const USERS = [
    "H. Al-Rashid", "M. Chen", "A. Farouk", "S. Khoury", "F. Tanaka", "R. Kumar", "L. Vasquez", "D. Weber",
];

const TEMPLATES = [
    { key: "upload_tb",     Icon: UploadSimple,     tone: "text-fp-navy",       verb: "uploaded Trial Balance for",   accent: "#2B4C7E" },
    { key: "upload_stat",   Icon: UploadSimple,     tone: "text-fp-teal",       verb: "uploaded Statistical KPIs for", accent: "#3B7A73" },
    { key: "approve",       Icon: CheckCircle,      tone: "text-fp-success",    verb: "approved submission from",     accent: "#2D6A4F" },
    { key: "ic_matched",    Icon: ArrowsLeftRight,  tone: "text-fp-plum",       verb: "reconciled IC balance with",   accent: "#7B3F5C" },
    { key: "extension",     Icon: Clock,            tone: "text-fp-saffron",    verb: "granted extension to",         accent: "#E19825" },
    { key: "ai_insight",    Icon: Sparkle,          tone: "text-fp-ink",        verb: "generated board briefing for", accent: "#0A2A22" },
    { key: "elimination",   Icon: Coins,            tone: "text-fp-terracotta", verb: "eliminated $2.4M IC balance for", accent: "#C05E4C" },
];

const randomEvent = (idOffset) => {
    const t = TEMPLATES[Math.floor(Math.random() * TEMPLATES.length)];
    const e = ENTITIES[Math.floor(Math.random() * ENTITIES.length)];
    const u = USERS[Math.floor(Math.random() * USERS.length)];
    return {
        id: `evt-${Date.now()}-${idOffset}`,
        actor: u,
        verb: t.verb,
        target: e,
        Icon: t.Icon,
        tone: t.tone,
        accent: t.accent,
        secondsAgo: 0,
    };
};

export default function LiveActivity() {
    const [events, setEvents] = useState(() => {
        // Seed with 5 events pre-aged
        const seed = [];
        for (let i = 0; i < 5; i++) {
            const e = randomEvent(i);
            e.secondsAgo = (i + 1) * 12 + Math.floor(Math.random() * 15);
            seed.push(e);
        }
        return seed;
    });
    const timerRef = useRef();

    useEffect(() => {
        const tick = () => {
            setEvents((prev) => {
                const aged = prev.map((e) => ({ ...e, secondsAgo: e.secondsAgo + 1 }));
                // ~20% chance per tick to inject a fresh event
                if (Math.random() < 0.22) {
                    return [randomEvent(Math.random()), ...aged].slice(0, 8);
                }
                return aged.slice(0, 8);
            });
            timerRef.current = setTimeout(tick, 1000 + Math.random() * 2000);
        };
        timerRef.current = setTimeout(tick, 2000);
        return () => timerRef.current && clearTimeout(timerRef.current);
    }, []);

    const fmtAge = (s) => {
        if (s < 60) return `${s}s ago`;
        return `${Math.floor(s / 60)}m ago`;
    };

    return (
        <div className="bg-white border border-fp-border h-full flex flex-col" data-testid="live-activity">
            <div className="px-5 py-3 border-b border-fp-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fp-success opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-fp-success" />
                    </span>
                    <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-fp-secondary">Live Activity</div>
                </div>
                <div className="font-mono text-[9px] text-fp-muted uppercase">real-time</div>
            </div>
            <div className="flex-1 divide-y divide-fp-border overflow-hidden">
                {events.map((e, i) => {
                    const Icon = e.Icon;
                    return (
                        <div
                            key={e.id}
                            className={`px-5 py-2.5 flex items-start gap-2.5 ${i === 0 ? "animate-fade-up" : ""}`}
                            style={i === 0 ? { animationDuration: "600ms" } : undefined}
                        >
                            <Icon size={13} weight="fill" className={`shrink-0 mt-0.5 ${e.tone}`} />
                            <div className="min-w-0 flex-1">
                                <div className="font-sans text-xs text-fp-ink leading-snug">
                                    <span className="font-medium">{e.actor}</span>
                                    <span className="text-fp-secondary"> {e.verb} </span>
                                    <span className="font-medium">{e.target}</span>
                                </div>
                                <div className="font-mono text-[9px] text-fp-muted uppercase tracking-wider mt-0.5">{fmtAge(e.secondsAgo)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
